import { Button } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { apiClient } from "@/lib/api/client";

type ConcernedDocument = {
  documentVersionId: number;
  documentId: number;
  label: string;
  index: string | null;
};

type Observation = {
  id: number;
  description: string;
  themeId: number | null;
  themeName: string | null;
  reportId: number;
  reportNumero: string | null;
  reportContractCode: string | null;
  authorName: string | null;
  noticeId: number;
  nAvis: string | null;
  noticeStatusLabel: string | null;
  concernedDocuments: ConcernedDocument[];
  treatedAt: string | null;
};

type FetchState =
  | { status: "loading" }
  | { status: "forbidden" }
  | { status: "error"; error: string }
  | { status: "success"; items: Observation[] };

/**
 * Screen — "Observations sans réponse".
 *
 * Vue de pilotage (contrat travaux rang 1) listant les observations qui
 * appellent une réponse mais dont l'ébauche est encore vide. Pour chaque
 * observation on rappelle le rapport, l'auteur, le thème et les documents
 * liés, avec un accès direct à l'écran de traitement.
 */
export function ObservationsSansReponse() {
  const { yardId } = useParams<{ yardId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [reportFilter, setReportFilter] = useState<string>("");
  const [themeFilter, setThemeFilter] = useState<string>("");

  const fetchObservations = useCallback(async () => {
    if (isNil(yardId)) return;
    setState({ status: "loading" });
    try {
      const { data, error, response } = await apiClient.GET(
        "/api/yards/{yardId}/observations/pending-response",
        { params: { path: { yardId } } },
      );
      if (response.status === 403) {
        setState({ status: "forbidden" });
        return;
      }
      if (error || isNil(data)) {
        setState({
          status: "error",
          error: "Erreur lors du chargement des observations",
        });
        return;
      }
      setState({ status: "success", items: data.data.items });
    } catch {
      setState({
        status: "error",
        error: "Erreur lors du chargement des observations",
      });
    }
  }, [yardId]);

  useEffect(() => {
    fetchObservations();
  }, [fetchObservations]);

  const allItems = state.status === "success" ? state.items : [];

  const reportOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const obs of allItems) {
      map.set(obs.reportId, obs.reportNumero ?? `Rapport #${obs.reportId}`);
    }
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [allItems]);

  const themeOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const obs of allItems) {
      if (isNotNil(obs.themeId) && isNotNil(obs.themeName)) {
        map.set(obs.themeId, obs.themeName);
      }
    }
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [allItems]);

  const items = allItems.filter((obs) => {
    if (reportFilter !== "" && String(obs.reportId) !== reportFilter) {
      return false;
    }
    if (themeFilter !== "" && String(obs.themeId ?? "") !== themeFilter) {
      return false;
    }
    return true;
  });

  const goToTreatment = () => {
    if (isNotNil(yardId)) {
      navigate(`/yards/${yardId}/traiter-observations`);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-4 border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            Observations sans réponse
          </h2>
          {state.status === "success" && (
            <p className="mt-1 text-sm text-zinc-500">
              {items.length} observation{items.length > 1 ? "s" : ""} en attente
              de réponse
            </p>
          )}
        </div>
        {state.status === "success" && (
          <Button type="button" onClick={goToTreatment}>
            Traiter les observations
          </Button>
        )}
      </div>

      {state.status === "forbidden" ? (
        <div className="flex flex-1 items-center justify-center px-4 text-center">
          <p className="text-sm text-zinc-500">
            Cette vue de pilotage est réservée aux pilotes de contrat travaux
            rang 1.
          </p>
        </div>
      ) : state.status === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-red-600">{state.error}</p>
        </div>
      ) : (
        <>
          {state.status === "success" && allItems.length > 0 && (
            <div className="flex flex-wrap gap-3 border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
              <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300">
                Rapport
                <select
                  value={reportFilter}
                  onChange={(e) => setReportFilter(e.target.value)}
                  className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                >
                  <option value="">Tous</option>
                  {reportOptions.map((o) => (
                    <option key={o.id} value={String(o.id)}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300">
                Thème
                <select
                  value={themeFilter}
                  onChange={(e) => setThemeFilter(e.target.value)}
                  className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                >
                  <option value="">Tous</option>
                  {themeOptions.map((o) => (
                    <option key={o.id} value={String(o.id)}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {state.status === "loading" ? (
              <p className="text-sm text-zinc-500">Chargement...</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-zinc-500">
                Aucune observation sans réponse
              </p>
            ) : (
              <ul className="space-y-4">
                {items.map((obs) => (
                  <li
                    key={obs.id}
                    className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-800"
                  >
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {obs.description}
                    </p>
                    <p className="mt-1 text-xs text-zinc-500">
                      Rapport {obs.reportNumero ?? "-"} · Avis{" "}
                      {obs.nAvis ?? "-"}
                      {obs.reportContractCode
                        ? ` · ${obs.reportContractCode}`
                        : ""}
                      {obs.authorName ? ` · Auteur : ${obs.authorName}` : ""}
                      {obs.themeName ? ` · ${obs.themeName}` : ""}
                    </p>

                    {obs.concernedDocuments.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-zinc-500">
                          Documents liés
                        </p>
                        <ul className="mt-1 flex flex-wrap gap-1.5">
                          {obs.concernedDocuments.map((doc) => (
                            <li
                              key={doc.documentVersionId}
                              className="rounded bg-zinc-100 px-2 py-0.5 text-xs text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                              title={doc.label}
                            >
                              {doc.label}
                              {doc.index ? ` (${doc.index})` : ""}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
